import React from "react";
import type { ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import { formatBankAccount } from "@edaz/utils/bankAccount";
import { Check, CollectionCheck } from "@frui.ts/bootstrap";
import type TransferRefundsViewModel from "../../../../viewModels/finance/refundApprovals/transferRefunds/transferRefundsViewModel";
import type TransferRefundResponse from "entities/transferRefundResponse";
import type { ListColumnDefinition } from "../../../common/list";
import ListView from "../../../common/list";
import { formatAmount, formatDate } from "utils/localizations";
import TransferRefundsFilters from "./transferRefundsFilters";
import { ApprovalColumns } from "../../../common/approvalColumns";
import { emptyValue } from "utils/emptyValue";

const TransferRefundsView: ViewComponent<TransferRefundsViewModel> = observer(({ vm }) => {
  const columns: ListColumnDefinition<TransferRefundResponse>[] = [
    {
      title: <Check custom target={vm as { allItems?: boolean }} property="allItems" threeState={true} />,
      valueFormatter: props => {
        if (props.item.isSelectable) {
          return <CollectionCheck custom target={vm} property="selectedItems" disabled={false} value={props.item} />;
        }
      },
    },
    {
      title: vm.translateModelAttribute("licensePlate", "licensePlate"),
      valueFormatter: ({ item }) =>
        item.charge ? `${vm.countryCode(item.charge.vehicle.countryId)} - ${item.charge.vehicle.licensePlate}` : emptyValue,
      cellClassName: "font-weight-bold",
    },
    {
      title: vm.translateModelAttribute("transferOriginalPayment", "senderBankAccount"),
      cellProps: props => ({ style: { whiteSpace: "pre-line" } }),
      valueFormatter: ({ item }) => formatBankAccount(item.originalPayment?.senderBankAccount),
    },
    {
      title: vm.translateModelAttribute("transferOriginalPayment", "variableSymbol"),
      valueFormatter: ({ item }) => (item.originalPayment?.variableSymbol ? item.originalPayment.variableSymbol : emptyValue),
      numberProperty: true,
    },
    {
      title: vm.translateModelAttribute("transferOriginalPayment", "paidAmount"),
      valueFormatter: ({ item }) =>
        item.originalPayment?.paidAmount ? formatAmount(item.originalPayment.paidAmount) : emptyValue,
      numberProperty: true,
    },
    {
      title: vm.translateModelAttribute("transferOriginalPayment", "postingDate"),
      valueFormatter: ({ item }) =>
        item.originalPayment?.postingDate ? formatDate(new Date(item.originalPayment.postingDate)) : emptyValue,
      dateProperty: true,
    },
    {
      title: vm.translateModelAttribute("refund", "officialRecordId"),
      valueFormatter: ({ item }) => (item.officialRecordId ? item.officialRecordId : emptyValue),
    },
    {
      title: vm.translateModelAttribute("refund", "refundPaymentTemplateId"),
      valueFormatter: ({ item }) => vm.translateRefundReason(item.refundPaymentTemplateId),
    },
    {
      title: vm.translateModelAttribute("refund", "refundAmount"),
      valueFormatter: ({ item }) => formatAmount(item.refundAmount),
      numberProperty: true,
    },
    ...ApprovalColumns<TransferRefundResponse, TransferRefundsViewModel>(vm),
  ];

  return (
    <>
      <TransferRefundsFilters vm={vm} />
      <ListView columns={columns} vm={vm} showDetailWithList={true} hideTitle={true} hideButtons={true} />
    </>
  );
});

export default TransferRefundsView;
